import { useFormsTranslation } from '@/hooks/useTranslation';
import { ContactPartner, ContactPartnerFormData } from '@/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

// Contact partner validation schema with translations
const createContactPartnerSchema = (tForms: (key: string) => string) =>
  z.object({
    contactType: z
      .string()
      .min(1, tForms('contactPartner.validation.contactTypeRequired')),
    language: z
      .string()
      .min(1, tForms('contactPartner.validation.languageRequired')),
    title: z.string().min(1, tForms('contactPartner.validation.titleRequired')),
    firstName: z
      .string()
      .min(1, tForms('contactPartner.validation.firstNameRequired')),
    lastName: z
      .string()
      .min(1, tForms('contactPartner.validation.lastNameRequired')),
    function: z
      .string()
      .min(1, tForms('contactPartner.validation.functionRequired')),
    isRepresentative: z.boolean(),
    email: z
      .string()
      .min(1, tForms('contactPartner.validation.emailRequired'))
      .email(tForms('contactPartner.validation.emailInvalid')),
    phone: z.string().min(1, tForms('contactPartner.validation.phoneRequired')),
    mobile: z.string().optional(),
    street: z.string().optional(),
    houseNumber: z.string().optional(),
    postalCode: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
    state: z.string().optional(),
  });

export type ContactPartnerSchemaType = z.infer<
  ReturnType<typeof createContactPartnerSchema>
>;

interface UseContactPartnerFormProps {
  contactPartner?: ContactPartner;
  onSave: (data: ContactPartnerFormData) => void;
  isOpen: boolean;
}

/**
 * Custom hook for managing contact partner form state and validation
 * Follows single responsibility principle by separating form logic from UI
 */
export function useContactPartnerForm({
  contactPartner,
  onSave,
  isOpen,
}: UseContactPartnerFormProps) {
  const { t: tForms } = useFormsTranslation();

  // Create schema with translations
  const contactPartnerSchema = createContactPartnerSchema(tForms);

  const form = useForm<ContactPartnerSchemaType>({
    resolver: zodResolver(contactPartnerSchema),
    defaultValues: {
      contactType: '',
      language: '',
      title: '',
      firstName: '',
      lastName: '',
      function: '',
      isRepresentative: false,
      email: '',
      phone: '',
      mobile: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      country: '',
      state: '',
    },
  });

  // Initialize form data when dialog opens or contact partner changes
  useEffect(() => {
    if (isOpen) {
      if (contactPartner) {
        // Edit mode - populate form with existing data
        form.reset({
          contactType: contactPartner.contactType || '',
          language: contactPartner.language || '',
          title: contactPartner.title || '',
          firstName: contactPartner.firstName || '',
          lastName: contactPartner.lastName || '',
          function: contactPartner.function || '',
          isRepresentative: contactPartner.isRepresentative || false,
          email: contactPartner.email || '',
          phone: contactPartner.phone || '',
          mobile: contactPartner.mobile || '',
          street: contactPartner.street || '',
          houseNumber: contactPartner.houseNumber || '',
          postalCode: contactPartner.postalCode || '',
          city: contactPartner.city || '',
          country: contactPartner.country || '',
          state: contactPartner.state || '',
        });
      } else {
        // Add mode - reset to default values
        form.reset({
          contactType: '',
          language: '',
          title: '',
          firstName: '',
          lastName: '',
          function: '',
          isRepresentative: false,
          email: '',
          phone: '',
          mobile: '',
          street: '',
          houseNumber: '',
          postalCode: '',
          city: '',
          country: '',
          state: '',
        });
      }
    }
  }, [contactPartner, isOpen, form]);

  const handleSubmit = useCallback(
    (data: ContactPartnerSchemaType) => {
      // Transform the data to match the expected interface
      const formData: ContactPartnerFormData = {
        ...data,
      };
      onSave(formData);
    },
    [onSave]
  );

  return {
    form,
    handleSubmit,
    isEditing: !!contactPartner,
  };
}
